import type { GenerateInputs, GenerateMediaType, GenerateModel } from '@/api/generate.api';
import type { MediaAsset } from '@/types/api/assets';
import type { DesignAppResult } from '@/types/design-app.types';

/** Where an attached asset sits in the prompt box. */
export type InputSlot = 'reference' | 'firstFrame' | 'lastFrame';

/**
 * One input in the prompt box. A file that is still uploading has no `asset` yet. It already holds its place in
 * the order and in the limit, so the order stays the order the user added things in, and the user can't start
 * more uploads than fit. Like an agent chat attachment, it shows a local picture while it uploads, and a failed
 * upload stays, marked, until the user removes it.
 */
export type ComposerInput = {
  id: string;
  slot: InputSlot;
  mediaType: GenerateMediaType;
  asset?: MediaAsset;
  /** A `blob:` URL of the file, while it uploads. Only for a type the browser can draw. */
  previewUrl?: string;
  failed?: boolean;
};

/** An input whose upload has ended, or that never needed one. */
export type ReadyInput = ComposerInput & { asset: MediaAsset };

export const isReady = (input: ComposerInput): input is ReadyInput => input.asset !== undefined;

/** Free places left for references, in total and for each type that has its own limit. */
export type ReferenceRoom = { total: number; byType: Partial<Record<GenerateMediaType, number>> };

/** Why some inputs were left out after a model switch. */
export type FitReason = 'none' | 'limit' | 'videoLimit' | 'notTaken';

export const MEDIA_TYPES: readonly GenerateMediaType[] = ['image', 'video', 'audio'];

export const mediaTypeOf = (type: string): GenerateMediaType | undefined =>
  MEDIA_TYPES.find((mediaType) => mediaType === type);

/**
 * Two models of the same kind can share inputs. Moving to another kind clears every input without a message,
 * because the user picked a model that works another way. A model with references counts as a references model,
 * also when it has a first frame.
 */
export const inputKind = (model: Pick<GenerateModel, 'mode' | 'inputs'>): string =>
  `${model.mode}:${model.inputs.references ? 'references' : model.inputs.frames ? 'frames' : 'none'}`;

/** The types a slot takes. A frame is always one image. */
export const typesFor = (inputs: GenerateInputs, slot: InputSlot): readonly GenerateMediaType[] =>
  slot === 'reference' ? (inputs.references?.types ?? []) : ['image'];

export const hasSlot = (inputs: GenerateInputs, slot: InputSlot): boolean => {
  switch (slot) {
    case 'reference':
      return Boolean(inputs.references);
    case 'firstFrame':
      return Boolean(inputs.frames);
    case 'lastFrame':
      return Boolean(inputs.frames?.last);
  }
};

/** The run details a gallery result carries besides its asset. */
type ResultFields = Partial<Pick<DesignAppResult, 'inputIndex' | 'runId' | 'batchId' | 'runStartedAt'>>;

/**
 * A gallery result as a run input: only the asset, without the prompt and run details the result carries.
 * Undefined for a type no model here takes, such as 3D.
 */
export function toMediaAsset(result: MediaAsset & ResultFields): MediaAsset | undefined {
  if (!mediaTypeOf(result.type)) {
    return undefined;
  }
  const {
    name: _name,
    input: _input,
    inputIndex: _inputIndex,
    runId: _runId,
    batchId: _batchId,
    runStartedAt: _runStartedAt,
    ...asset
  } = result;
  return asset;
}

/** Everything takes a place in the limit: references, a first frame next to them, and running uploads. */
export function referenceRoom(inputs: GenerateInputs, placed: readonly ComposerInput[]): ReferenceRoom {
  const references = inputs.references;
  if (!references) {
    return { total: 0, byType: {} };
  }
  const byType: Partial<Record<GenerateMediaType, number>> = {};
  for (const [mediaType, max] of Object.entries(references.maxByType ?? {})) {
    const type = mediaTypeOf(mediaType);
    if (type && max !== undefined) {
      byType[type] = max - placed.filter((input) => input.slot === 'reference' && input.mediaType === type).length;
    }
  }
  return { total: references.max - placed.length, byType };
}

/** Whether `mediaTypes` all fit in `room`. Null when they do. */
export function overLimit(
  room: ReferenceRoom,
  mediaTypes: readonly GenerateMediaType[],
): 'limit' | 'videoLimit' | null {
  if (mediaTypes.length > room.total) {
    return 'limit';
  }
  const videos = mediaTypes.filter((type) => type === 'video').length;
  return room.byType.video !== undefined && videos > room.byType.video ? 'videoLimit' : null;
}

/**
 * The inputs `to` keeps when the user switches to it from a model of the same kind. Inputs of a type it doesn't
 * take go first, then the first ones that fit its limits stay, in order. When both happen, the reason is the type,
 * since the limit message would name the wrong cause. Uploads still running follow the same rules, so one that
 * ends later is placed as if it had ended before the switch.
 */
export function fitInputs(
  attached: readonly ComposerInput[],
  to: GenerateInputs,
): { kept: ComposerInput[]; reason: FitReason } {
  let reason: FitReason = 'none';
  const note = (next: FitReason) => {
    if (reason !== 'notTaken') {
      reason = next;
    }
  };
  const taken = attached.filter((input) => {
    const fits = hasSlot(to, input.slot) && typesFor(to, input.slot).includes(input.mediaType);
    if (!fits) {
      note('notTaken');
    }
    return fits;
  });
  // A first frame keeps its place before references, as it is listed first.
  const ordered = [
    ...taken.filter((input) => input.slot !== 'reference'),
    ...taken.filter((input) => input.slot === 'reference'),
  ];
  const kept: ComposerInput[] = [];
  for (const input of ordered) {
    const blocked = input.slot === 'reference' ? overLimit(referenceRoom(to, kept), [input.mediaType]) : null;
    if (blocked) {
      note(blocked);
      continue;
    }
    kept.push(input);
  }
  return { kept: attached.filter((input) => kept.includes(input)), reason };
}

/**
 * The run input for the attached assets: each frame under its own key, and references numbered in order.
 * Generate waits for every upload, so by then every input is ready.
 */
export function toRunInputs(inputs: GenerateInputs, attached: readonly ComposerInput[]): Record<string, MediaAsset> {
  const runInputs: Record<string, MediaAsset> = {};
  const ready = attached.filter(isReady);
  const references = ready.filter((input) => input.slot === 'reference');
  references.forEach((input, index) => {
    if (inputs.references) {
      runInputs[`${inputs.references.keyPrefix}_${index + 1}`] = input.asset;
    }
  });
  const first = ready.find((input) => input.slot === 'firstFrame');
  const last = ready.find((input) => input.slot === 'lastFrame');
  if (first && inputs.frames) {
    runInputs[inputs.frames.first] = first.asset;
  }
  if (last && inputs.frames?.last) {
    runInputs[inputs.frames.last] = last.asset;
  }
  return runInputs;
}
